import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeftIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Textarea } from '../../components/common/Textarea';
import { Checkbox } from '../../components/common/Checkbox';
import { CategorySelector } from '../../components/pim/CategorySelector';
import { MediaUploader } from '../../components/media/MediaUploader';
import { MediaGallery } from '../../components/media/MediaGallery';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  Product, 
  ProductFormData, 
  PimAttribute,
  PimAttributeGroup,
  ProductType,
  PimAttributeType 
} from '../../types/pim';

// Validation schema
const productSchema = z.object({
  sku: z.string().min(1, 'SKU jest wymagane').max(100, 'SKU może mieć maksymalnie 100 znaków'),
  name: z.string().min(1, 'Nazwa jest wymagana').max(255, 'Nazwa może mieć maksymalnie 255 znaków'),
  slug: z.string().optional(),
  status: z.enum(['draft', 'published']),
  description: z.string().optional(),
  product_type_id: z.string().optional().transform((val) => {
    if (!val || val === '') return undefined;
    const num = parseInt(val);
    return isNaN(num) ? undefined : num;
  }),
  // Category selection
  categoryIds: z.array(z.number()).default([]),
  primaryCategoryId: z.number().optional(),
  // Dynamic attributes as a single object - use passthrough to allow any keys
  dynamicAttributes: z.record(z.string(), z.any()).default({}).optional()
});

type ProductFormValues = z.infer<typeof productSchema>;

// Dynamic attribute input component
const DynamicAttributeInput: React.FC<{
  attribute: PimAttribute;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}> = ({ attribute, value, onChange, error }) => {
  const renderInput = () => {
    switch (attribute.type) {
      case 'string':
        return (
          <Input
            placeholder={`Wprowadź ${(attribute.label || 'atrybut').toLowerCase()}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            required={Boolean(attribute.is_required)}
          />
        );

      case 'text':
        return (
          <Textarea
            placeholder={`Wprowadź ${(attribute.label || 'atrybut').toLowerCase()}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            required={Boolean(attribute.is_required)}
            rows={4}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={`Wprowadź ${(attribute.label || 'atrybut').toLowerCase()}`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
            error={error}
            required={Boolean(attribute.is_required)}
          />
        );

      case 'boolean':
        return (
          <Checkbox
            checked={value || false}
            onChange={(e) => onChange(e.target.checked)}
            label={`Włącz ${(attribute.label || 'atrybut').toLowerCase()}`}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            required={Boolean(attribute.is_required)}
          />
        );

      case 'enum':
        const options = attribute.options_json?.options || [];
        return (
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            options={[
              ...(Boolean(attribute.is_required) ? [] : [{ value: '', label: 'Wybierz opcję' }]),
              ...options.map(option => ({ value: option, label: option }))
            ]}
            error={error}
            required={Boolean(attribute.is_required)}
          />
        );

      case 'multiselect':
        const multiselectOptions = attribute.options_json?.options || [];
        return (
          <div className="space-y-2">
            {multiselectOptions.map(option => (
              <Checkbox
                key={option}
                checked={(value || []).includes(option)}
                onChange={(e) => {
                  const currentValue = value || [];
                  if (e.target.checked) {
                    onChange([...currentValue, option]);
                  } else {
                    onChange(currentValue.filter((v: string) => v !== option));
                  }
                }}
                label={option}
              />
            ))}
          </div>
        );

      case 'json':
        return (
          <Textarea
            placeholder={`Wprowadź JSON dla ${(attribute.label || 'atrybut').toLowerCase()}`}
            value={value ? JSON.stringify(value, null, 2) : ''}
            onChange={(e) => {
              try {
                const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                onChange(parsed);
              } catch {
                // Keep the raw value if JSON is invalid
                onChange(e.target.value);
              }
            }}
            error={error}
            required={Boolean(attribute.is_required)}
            rows={6}
            className="font-mono text-sm"
          />
        );

      default:
        return (
          <Input
            placeholder={`Wprowadź ${(attribute.label || 'atrybut').toLowerCase()}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            required={Boolean(attribute.is_required)}
          />
        );
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {attribute.label || 'Atrybut'}
        {Boolean(attribute.is_required) && <span className="text-red-500 ml-1">*</span>}
      </label>
      {attribute.description && (
        <p className="text-sm text-gray-500">{attribute.description}</p>
      )}
      {renderInput()}
    </div>
  );
};

type ProductFormProps = { productId?: string };

export const ProductForm: React.FC<ProductFormProps> = ({ productId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const route = useParams<{ id: string }>();
  const id = productId || route.id;
  const isEdit = Boolean(id);
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // Media state
  const [productMedia, setProductMedia] = useState<any[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [pendingMedia, setPendingMedia] = useState<File[]>([]); // Media waiting to be uploaded after product save

  // Fetch product media when editing
  useEffect(() => {
    if (isEdit && id) {
      const fetchMedia = async () => {
        try {
          setIsLoadingMedia(true);
          const response = await pimService.getProductMedia(parseInt(id));
          setProductMedia(response.data || []);
        } catch (error) {
        } finally {
          setIsLoadingMedia(false);
        }
      };
      fetchMedia();
    }
  }, [isEdit, id]);

  // Helper function to extract attribute value from API response
  const getAttributeValue = useCallback((attributeValue: any) => {
    const attribute = attributeValue.attribute;
    switch (attribute.type) {
      case 'string':
      case 'enum':
        return attributeValue.value_string;
      case 'text':
        return attributeValue.value_text;
      case 'number':
        return attributeValue.value_number;
      case 'boolean':
        return attributeValue.value_boolean;
      case 'date':
        return attributeValue.value_date;
      case 'json':
      case 'multiselect':
        return attributeValue.value_json;
      default:
        return null;
    }
  }, []);

  // Fetch product data for editing
  const { data: productData, isLoading: loadingProduct } = useQuery({
    queryKey: ['pim-product', id],
    queryFn: () => pimService.getProduct(parseInt(id!)),
    enabled: isEdit,
    select: (response) => response.data
  });

  // Fetch product types
  const { data: productTypesData } = useQuery({
    queryKey: ['pim-product-types-dropdown'],
    queryFn: () => pimService.getProductTypesForDropdown(),
    select: (response) => response.data
  });

  // Fetch attribute groups with attributes
  const { data: attributeGroupsData } = useQuery({
    queryKey: ['pim-attribute-groups-with-attributes'],
    queryFn: () => pimService.getAttributeGroups({ limit: 100 }),
    select: (response) => response.data
  });

  // Fetch attributes for the selected product type
  const { data: attributesData } = useQuery({
    queryKey: ['pim-attributes-for-product'],
    queryFn: () => pimService.getAttributes({
      limit: 100
    }),
    select: (response) => response.data
  });

  // Calculate default values based on whether we're editing or creating
  const defaultValues = useMemo((): ProductFormValues => {
    if (isEdit && productData) {
      const product = productData;
      
      // Process dynamic attributes
      const dynamicAttributes: Record<string, any> = {};
      if (product.attributeValues && Array.isArray(product.attributeValues)) {
        product.attributeValues.forEach((av: any) => {
          if (av.attribute && av.attribute.code) {
            const value = getAttributeValue(av);
            if (value !== null && value !== undefined) {
              dynamicAttributes[av.attribute.code] = value;
            }
          }
        });
      }

      // Process categories
      const categoryIds = product.categories ? product.categories.map((cat: any) => cat.id) : [];
      const primaryCategory = product.categories?.find((cat: any) => 
        cat.ProductCategory?.is_primary || cat.productCategory?.is_primary
      );

      return {
        sku: product.sku || '',
        name: product.name || '',
        slug: product.slug || '',
        status: product.status || 'draft',
        description: product.description || '',
        product_type_id: product.product_type_id ? String(product.product_type_id) : '',
        categoryIds,
        primaryCategoryId: primaryCategory?.id,
        dynamicAttributes
      };
    }

    // Default values for new product
    return {
      sku: '',
      name: '',
      slug: '',
      status: 'draft',
      description: '',
      product_type_id: '',
      categoryIds: [],
      primaryCategoryId: undefined,
      dynamicAttributes: {}
    };
  }, [isEdit, productData, getAttributeValue]);

  // Form setup with React Hook Form
  const methods = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues,
    // Reset form when default values change (when product data loads)
    values: defaultValues
  });

  const { handleSubmit, control, watch, setValue, formState: { errors, isSubmitting } } = methods;

  // Watch product type to show relevant attributes
  const selectedProductTypeId = watch('product_type_id');
  const watchedDynamicAttributes = watch('dynamicAttributes');

  // Auto-generate slug from name
  const handleNameChange = useCallback((name: string) => {
    setValue('name', name);
    if (!isEdit && name) {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setValue('slug', slug);
    }
  }, [isEdit, setValue]);

  // Handle dynamic attribute changes
  const handleDynamicAttributeChange = useCallback((attributeCode: string, value: any) => {
    const currentDynamicAttributes = watch('dynamicAttributes') || {};
    setValue('dynamicAttributes', {
      ...currentDynamicAttributes,
      [attributeCode]: value
    });
  }, [setValue, watch]);

  // Handle category selection
  const handleCategoryChange = useCallback((categoryIds: number[], primaryCategoryId?: number) => {
    setValue('categoryIds', categoryIds);
    setValue('primaryCategoryId', primaryCategoryId);
  }, [setValue]);

  // Media handlers
  const handleMediaUpload = useCallback(async (uploadData: any[]) => {
    const files = uploadData.map(data => data.file);

    // If product is not saved yet, store files as pending uploads
    if (!isEdit || !id) {
      setPendingMedia(prev => [...prev, ...files]);
      showNotification(
        `${files.length} ${files.length === 1 ? 'plik' : 'plików'} zostanie przesłanych po zapisaniu produktu`,
        'info'
      );
      return;
    }

    // If product exists, upload immediately
    try {
      await pimService.uploadProductMedia(parseInt(id), files);

      // Refresh media list
      const response = await pimService.getProductMedia(parseInt(id));
      setProductMedia(response.data || []);

      showNotification('Media zostały pomyślnie przesłane', 'success');
    } catch (error) {
      showNotification('Nie udało się przesłać mediów', 'error');
    }
  }, [isEdit, id, showNotification]);

  const handleMediaEdit = useCallback(async (media: any) => {
    // This would open a modal for editing media details
  }, []);

  const handleMediaDelete = useCallback(async (media: any) => {
    if (!isEdit || !id) return;

    try {
      await pimService.deleteProductMedia(parseInt(id), media.id);
      setProductMedia(prev => prev.filter(m => m.id !== media.id));
      showNotification('Media zostały pomyślnie usunięte', 'success');
    } catch (error) {
      showNotification('Nie udało się usunąć mediów', 'error');
    }
  }, [isEdit, id, showNotification]);

  const handlePendingMediaRemove = useCallback((index: number) => {
    setPendingMedia(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleMediaDownload = useCallback((media: any) => {
    if (media.url) {
      const link = document.createElement('a');
      link.href = media.url;
      link.download = media.original_name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, []);

  const handleMediaPreview = useCallback((media: any) => {
    if (media.url) {
      window.open(media.url, '_blank');
    }
  }, []);

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: async (data: ProductFormValues) => {
      // Convert form data to API format
      const formattedData: ProductFormData = {
        sku: data.sku,
        name: data.name,
        slug: data.slug,
        status: data.status,
        description: data.description,
        product_type_id: data.product_type_id,
        dynamicAttributes: data.dynamicAttributes
      };
      
      let productResponse;
      if (isEdit) {
        productResponse = await pimService.updateProduct(parseInt(id!), formattedData);
      } else {
        productResponse = await pimService.createProduct(formattedData);
      }

      // Assign categories (always send during edit to update, or if there are categories to assign)
      // Backend returns { success, message, data: { product } }
      const productData = productResponse?.data?.product || productResponse?.data;
      const productId = isEdit ? parseInt(id!) : productData?.id;

      if (productId && (isEdit || data.categoryIds.length > 0)) {
        try {
          await pimService.assignProductCategories(productId, {
            categoryIds: data.categoryIds,
            primaryCategoryId: data.primaryCategoryId
          });
        } catch (categoryError: any) {
          // Don't throw - let product update succeed even if category assignment fails
          showNotification(
            'Produkt został zapisany, ale wystąpił błąd przy przypisywaniu kategorii: ' + (categoryError.response?.data?.message || categoryError.message),
            'warning'
          );
        }
      }

      return productResponse;
    },
    onSuccess: async (productResponse) => {
      // Extract product ID from response
      const productData = productResponse?.data?.product || productResponse?.data;
      const productId = productData?.id;

      // Upload pending media if any
      if (!isEdit && pendingMedia.length > 0 && productId) {
        try {
          showNotification('Przesyłanie mediów...', 'info');
          await pimService.uploadProductMedia(productId, pendingMedia);
          setPendingMedia([]); // Clear pending media after successful upload
          showNotification('Produkt i media zostały pomyślnie zapisane', 'success');
        } catch (error) {
          showNotification(
            'Produkt został utworzony, ale wystąpił błąd podczas przesyłania mediów',
            'warning'
          );
        }
      } else {
        showNotification(
          isEdit ? 'Produkt został pomyślnie zaktualizowany' : 'Produkt został pomyślnie utworzony',
          'success'
        );
      }

      queryClient.invalidateQueries({ queryKey: ['pim-products'] });
      navigate('/pim/products');
    },
    onError: (error: any) => {
      let errorMessage = error.message || `Błąd podczas ${isEdit ? 'aktualizacji' : 'tworzenia'} produktu`;

      // Handle specific error codes
      if (error.response?.status === 409) {
        errorMessage = `Produkt z SKU "${watch('sku')}" już istnieje. Użyj unikalnego SKU.`;
      } else if (error.response?.status === 400) {
        errorMessage = 'Nieprawidłowe dane formularza. Sprawdź wszystkie pola.';
      }

      showNotification(errorMessage, 'error');
    }
  });

  // Submit handler
  const onSubmit = (data: ProductFormValues) => {
    mutation.mutate(data);
  };

  // Group attributes by group for display
  // Get selected product type from form
  const selectedCategoryIds = watch('categoryIds') || [];

  const groupedAttributes = useMemo(() => {
    if (!attributeGroupsData || !attributesData) return {};

    // If no product type is selected, return empty object
    if (!selectedProductTypeId) return {};

    const productTypeIdNum = parseInt(selectedProductTypeId);
    if (isNaN(productTypeIdNum)) return {};

    return attributeGroupsData.reduce((acc, group) => {
      // Filter groups by selected product type
      // Include groups that are:
      // 1. Global (is_global = true)
      // 2. Assigned to the selected product type (product_type_id matches)
      const isGlobalGroup = group.is_global === true;
      const isAssignedToProductType = group.product_type_id === productTypeIdNum;

      if (isGlobalGroup || isAssignedToProductType) {
        // Filter attributes by group_id, is_public, and is_active
        // Only show attributes that are both public and active
        const groupAttributes = attributesData.filter(attr =>
          attr.group_id === group.id &&
          attr.is_public !== false &&
          attr.is_active !== false
        );
        if (groupAttributes.length > 0) {
          acc[group.id] = { group, attributes: groupAttributes };
        }
      }
      return acc;
    }, {} as Record<number, { group: PimAttributeGroup; attributes: PimAttribute[] }>);
  }, [attributeGroupsData, attributesData, selectedProductTypeId]);

  // Permission check
  if (!permissions.canWriteProducts) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Brak dostępu</h3>
        <p className="mt-1 text-sm text-gray-500">
          Nie masz uprawnień do {isEdit ? 'edycji' : 'tworzenia'} produktów.
        </p>
      </div>
    );
  }

  // Loading state
  if (isEdit && loadingProduct) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-gray-600">Ładowanie produktu...</p>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              icon={ArrowLeftIcon}
              onClick={() => navigate('/pim/products')}
            >
              Powrót
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit ? 'Edytuj Produkt' : 'Utwórz Produkt'}
              </h1>
              <p className="text-gray-600">
                {isEdit ? 'Zaktualizuj informacje o produkcie i atrybuty' : 'Dodaj nowy produkt do katalogu'}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/pim/products')}
            >
              Anuluj
            </Button>
            <Button
              type="submit"
              icon={CheckIcon}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Zapisywanie...' : 'Zapisz Produkt'}
            </Button>
          </div>
        </div>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Podstawowe Informacje</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="sku"
                control={control}
                render={({ field }) => (
                  <Input
                    label={t('pim.products.sku')}
                    {...field}
                    error={errors.sku?.message}
                    required
                  />
                )}
              />
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    label={t('pim.products.name')}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleNameChange(e.target.value);
                    }}
                    error={errors.name?.message}
                    required
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="slug"
                control={control}
                render={({ field }) => (
                  <Input
                    label={t('pim.products.slug')}
                    {...field}
                    error={errors.slug?.message}
                    help="Wersja nazwy przyjazna dla adresów URL"
                  />
                )}
              />
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('pim.products.statusLabel')}
                    {...field}
                    options={[
                      { value: 'draft', label: 'Wersja robocza' },
                      { value: 'published', label: 'Opublikowany' }
                    ]}
                    error={errors.status?.message}
                    required
                  />
                )}
              />
            </div>
            
            <Controller
              name="product_type_id"
              control={control}
              render={({ field }) => (
                <Select
                  label={t('pim.products.type')}
                  {...field}
                  options={[
                    { value: '', label: 'Wybierz typ produktu' },
                    ...(productTypesData || []).map(type => ({
                      value: type.id.toString(),
                      label: type.name
                    }))
                  ]}
                  error={errors.product_type_id?.message}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  label={t('pim.products.description')}
                  {...field}
                  error={errors.description?.message}
                  rows={4}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Categories */}
        <Card>
          <CardHeader>
            <CardTitle>Kategorie</CardTitle>
            <p className="text-sm text-gray-600">
              Przypisz produkt do odpowiednich kategorii. Możesz wybrać jedną jako główną.
            </p>
          </CardHeader>
          <CardContent>
            <Controller
              name="categoryIds"
              control={control}
              render={({ field: { value: categoryIds } }) => (
                <Controller
                  name="primaryCategoryId"
                  control={control}
                  render={({ field: { value: primaryCategoryId } }) => (
                    <CategorySelector
                      selectedCategoryIds={categoryIds}
                      primaryCategoryId={primaryCategoryId}
                      onSelectionChange={handleCategoryChange}
                      multiple={true}
                      label={t('pim.products.form.selectCategories')}
                      help="Kategorie pomagają organizować produkty w sklepie"
                    />
                  )}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Dynamic Attributes */}
        {!selectedProductTypeId ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <QuestionMarkCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Wybierz typ produktu</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Atrybuty produktu pojawią się po wybraniu typu produktu powyżej
                </p>
              </div>
            </CardContent>
          </Card>
        ) : Object.keys(groupedAttributes).length > 0 ? (
          <div className="space-y-6">
            {Object.values(groupedAttributes).map(({ group, attributes }) => (
              <Card key={group.id}>
                <CardHeader>
                  <CardTitle>{group.name}</CardTitle>
                  {group.description && (
                    <p className="text-sm text-gray-600">{group.description}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {attributes.map(attribute => (
                      <DynamicAttributeInput
                        key={attribute.id}
                        attribute={attribute}
                        value={watchedDynamicAttributes[attribute.code]}
                        onChange={(value) => handleDynamicAttributeChange(attribute.code, value)}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-amber-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Brak atrybutów</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Dla wybranego typu produktu nie ma przypisanych grup atrybutów
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Możesz przypisać grupy atrybutów do tego typu produktu w ustawieniach PIM
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Media Management */}
        <Card role="region" aria-labelledby="media-section-title">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle id="media-section-title" className="flex items-center gap-2">
                <PhotoIcon className="h-5 w-5" aria-hidden="true" />
                Media Produktu
                {productMedia.length > 0 && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full ml-2">
                    {productMedia.length} {productMedia.length === 1 ? 'plik' : 'plików'}
                  </span>
                )}
              </CardTitle>
              <div className="text-sm text-gray-500">
                {productMedia.length}/20 plików
              </div>
            </div>
            <p className="text-sm text-gray-600" id="media-section-description">
              {isEdit
                ? 'Zarządzaj zdjęciami, dokumentami i innymi plikami multimedialnymi produktu.'
                : 'Dodaj zdjęcia i pliki multimedialne. Możesz je uploadować przed zapisaniem produktu.'}
            </p>
          </CardHeader>
          <CardContent aria-describedby="media-section-description" className="space-y-6">
            <div className="space-y-4">
              {/* Empty state guidance */}
              {productMedia.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 mr-3" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-800">Brak plików multimedialnych</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Produkty ze zdjęciami osiągają lepsze wyniki. Rozważ dodanie:
                      </p>
                      <ul className="text-xs text-amber-600 mt-2 list-disc list-inside space-y-1">
                        <li>Głównego zdjęcia produktu (zalecane: 1200x1200px)</li>
                        <li>Dodatkowych widoków z różnych kątów</li>
                        <li>Dokumentacji produktu lub specyfikacji technicznej</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile-specific guidance */}
              <div className="sm:hidden mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    📱 Dotknij poniżej aby wybrać pliki z urządzenia lub aparatu
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Dodaj Nowe Media</h4>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    title="Obsługiwane formaty: Obrazy (JPG, PNG, WebP), PDF oraz dokumenty Office. Max 50MB na plik, do 20 plików."
                  >
                    <QuestionMarkCircleIcon className="h-4 w-4" />
                  </button>
                </div>
                <MediaUploader
                  onUpload={handleMediaUpload}
                  productId={id ? parseInt(id) : undefined}
                  maxFiles={20}
                  maxFileSize={50 * 1024 * 1024} // 50MB
                  accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  aria-label={t('pim.products.form.uploadMedia')}
                />
              </div>

              {/* Pending Media Preview (before save) */}
              {!isEdit && pendingMedia.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Media do przesłania ({pendingMedia.length})
                  </h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-800">
                        {pendingMedia.length} {pendingMedia.length === 1 ? 'plik zostanie przesłany' : 'plików zostanie przesłanych'} automatycznie po zapisaniu produktu
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {pendingMedia.map((file, index) => (
                      <div key={`pending-${index}`} className="relative group border rounded-lg overflow-hidden bg-gray-50">
                        {file.type.startsWith('image/') ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full aspect-square object-cover"
                          />
                        ) : (
                          <div className="w-full aspect-square flex flex-col items-center justify-center bg-gray-100">
                            <PhotoIcon className="h-12 w-12 text-gray-400 mb-2" />
                            <span className="text-xs text-gray-600 px-2 text-center">{file.name}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handlePendingMediaRemove(index)}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                          title="Usuń z kolejki"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <p className="text-xs text-white truncate">{file.name}</p>
                          <p className="text-xs text-gray-300">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing Media Gallery */}
              {isEdit && productMedia.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Aktualne Media ({productMedia.length})
                  </h4>
                  {isLoadingMedia ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-gray-200 animate-pulse rounded-lg aspect-square"></div>
                      ))}
                    </div>
                  ) : (
                    <MediaGallery
                      media={productMedia}
                      loading={isLoadingMedia}
                      onEdit={handleMediaEdit}
                      onDelete={handleMediaDelete}
                      onDownload={handleMediaDownload}
                      onPreview={handleMediaPreview}
                      className="max-w-none"
                    />
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </FormProvider>
  );
};